import { useState, type FormEvent } from "react";
import { useTranslation } from "@/i18n";
import { MSG } from "@ease/shared/i18n/message-keys";

const PLANS = [
  { value: "free", labelKey: MSG.admin.provision.plans.free, price: "€0/mo" },
  { value: "starter", labelKey: MSG.admin.provision.plans.starter, price: "€89/mo" },
  { value: "growth", labelKey: MSG.admin.provision.plans.growth, price: "€249/mo" },
  { value: "scale", labelKey: MSG.admin.provision.plans.scale, price: "€449/mo" },
];

/** F.2: Tenant Provisioning — create, suspend, delete tenants */
export default function AdminProvisionPage() {
  const { t } = useTranslation();
  const [showCreate, setShowCreate] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [slug, setSlug] = useState("");
  const [plan, setPlan] = useState("free");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [isProvisioning, setIsProvisioning] = useState(false);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setIsProvisioning(true);
    // Wire to api-platform provisioning endpoint (creates user + org + tenant + Neon DB + subscription)
    setTimeout(() => setIsProvisioning(false), 2000);
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">{t(MSG.admin.provision.title)}</h1>
        <button onClick={() => setShowCreate(!showCreate)}
          className="rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-primary)]/90">
          {t(MSG.admin.provision.createTenant)}
        </button>
      </div>

      {showCreate && (
        <form onSubmit={handleCreate} className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] p-6 space-y-4">
          <h3 className="font-semibold text-white">{t(MSG.admin.provision.newTenant)}</h3>

          <div>
            <label className="block text-sm font-medium text-muted-foreground">{t(MSG.admin.provision.businessName)}</label>
            <input type="text" required value={businessName} onChange={(e) => { setBusinessName(e.target.value); setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, "-").replace(/-+/g, "-")); }}
              className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-white" />
          </div>

          <div>
            <label className="block text-sm font-medium text-muted-foreground">{t(MSG.admin.provision.slug)}</label>
            <div className="mt-1 flex items-center rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]">
              <input type="text" required value={slug} onChange={(e) => setSlug(e.target.value)}
                className="flex-1 bg-transparent px-3 py-2 text-sm text-white" />
              <span className="px-3 text-xs text-muted-foreground">.stero.ai</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-muted-foreground">{t(MSG.admin.provision.plan)}</label>
            <select value={plan} onChange={(e) => setPlan(e.target.value)}
              className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-white">
              {PLANS.map((p) => (
                <option key={p.value} value={p.value}>{t(p.labelKey)} ({p.price})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-muted-foreground">{t(MSG.admin.provision.ownerEmail)}</label>
            <input type="email" required value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)}
              placeholder={t(MSG.admin.provision.ownerEmailPlaceholder)}
              className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-white" />
          </div>

          <div className="rounded-md bg-[var(--color-surface)] p-3 text-xs text-muted-foreground">
            {t(MSG.admin.provision.createFlowSummary)}
          </div>

          <div className="flex gap-3">
            <button type="submit" disabled={isProvisioning}
              className="rounded-md bg-[var(--color-primary)] px-6 py-2 text-sm font-medium text-white hover:bg-[var(--color-primary)]/90 disabled:opacity-50">
              {isProvisioning ? t(MSG.admin.provision.provisioning) : t(MSG.admin.provision.actions.createTenant)}
            </button>
            <button type="button" onClick={() => setShowCreate(false)}
              className="rounded-md border border-[var(--color-border)] px-3 py-2 text-sm text-muted-foreground hover:bg-[var(--color-surface)]">
              {t(MSG.admin.provision.actions.cancel)}
            </button>
          </div>
        </form>
      )}

      <section>
        <h2 className="text-lg font-semibold text-white">{t(MSG.admin.provision.tenantActions)}</h2>
        <div className="mt-4 space-y-3">
          <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            <p className="text-sm text-muted-foreground">{t(MSG.admin.provision.actionDescription)}</p>
            <p className="mt-2 text-xs text-muted-foreground">{t(MSG.admin.provision.actionWarning)}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
